import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { AuthContext } from '../context/AuthContext';
import toast from 'react-hot-toast';
import SEO from '../components/SEO';
import {
    Cog6ToothIcon,
    KeyIcon,
    EnvelopeIcon,
    BellIcon,
    TrashIcon,
    ShieldCheckIcon,
    EyeIcon,
    EyeSlashIcon
} from '@heroicons/react/24/outline';

const SettingsPage = () => {
    const { user, token, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    
    const [activeTab, setActiveTab] = useState('password');
    const [loading, setLoading] = useState(false);
    
    // Password Change
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    
    // Email Change
    const [newEmail, setNewEmail] = useState('');
    const [emailPassword, setEmailPassword] = useState('');
    
    // Notification Preferences
    const [notificationPrefs, setNotificationPrefs] = useState({
        news: true,
        research: true,
        info: true,
        courses: true,
        email: true
    });
    
    // Account Deletion
    const [deletePassword, setDeletePassword] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        if (!token) {
            navigate('/login');
            return;
        }
        
        // Google kullanıcıları için şifre ve email tablarını gizle
        if (user?.authProvider === 'google' && (activeTab === 'password' || activeTab === 'email')) {
            setActiveTab('notifications');
        }
        
        // Kullanıcının mevcut bildirim tercihlerini yükle
        if (user?.notificationPreferences) {
            setNotificationPrefs(user.notificationPreferences);
        }
    }, [token, user, navigate, activeTab]);

    // Change Password
    const handlePasswordChange = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }
        
        if (newPassword.length < 8) {
            toast.error('New password must be at least 8 characters');
            return;
        }
        
        setLoading(true);
        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.put(`${API_BASE_URL}/api/users/settings/password`, {
                currentPassword,
                newPassword
            }, config);
            
            toast.success('Password changed successfully');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    // Change Email
    const handleEmailChange = async (e) => {
        e.preventDefault();
        
        setLoading(true);
        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.put(`${API_BASE_URL}/api/users/settings/email`, {
                newEmail,
                password: emailPassword
            }, config);
            
            toast.success('Email changed successfully. Please login again.');
            setNewEmail('');
            setEmailPassword('');
            
            // Logout user
            setTimeout(() => {
                logout();
                navigate('/login');
            }, 2000);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Failed to change email');
        } finally {
            setLoading(false);
        }
    };

    // Update Notification Preferences
    const handleNotificationUpdate = async (key, value) => {
        const newPrefs = { ...notificationPrefs, [key]: value };
        setNotificationPrefs(newPrefs);
        
        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.put(`${API_BASE_URL}/api/users/settings/notifications`, newPrefs, config);
            toast.success('Notification preferences updated');
        } catch (err) {
            toast.error('Failed to update preferences');
            // Revert
            setNotificationPrefs(notificationPrefs);
        }
    };

    // Delete Account
    const handleDeleteAccount = async () => {
        if (!deletePassword) {
            toast.error('Please enter your password');
            return;
        }
        
        setLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/users/account`, {
                headers: { 'x-auth-token': token },
                data: { password: deletePassword }
            });
            
            toast.success('Your account has been deleted');
            setTimeout(() => {
                logout();
                navigate('/');
            }, 2000);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Failed to delete account');
        } finally {
            setLoading(false);
        }
    };

    const tabs = [
        ...(user?.authProvider !== 'google' ? [{ id: 'password', label: 'Change Password', icon: KeyIcon }] : []),
        ...(user?.authProvider !== 'google' ? [{ id: 'email', label: 'Change Email', icon: EnvelopeIcon }] : []),
        { id: 'notifications', label: 'Notifications', icon: BellIcon },
        { id: 'delete', label: 'Delete Account', icon: TrashIcon }
    ];

    return (
        <>
            <SEO 
                title="Settings" 
                description="Manage your account settings"
                noindex={true}
            />
            
            <div className="min-h-screen bg-light-bg dark:bg-dark-bg py-12">
                <div className="container mx-auto px-4 max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-light-card dark:bg-dark-card rounded-lg shadow-lg overflow-hidden"
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-primary-500 to-blue-600 dark:from-primary-600 dark:to-cyan-600 p-6 text-white">
                            <div className="flex items-center gap-3">
                                <Cog6ToothIcon className="h-8 w-8" />
                                <div>
                                    <h1 className="text-2xl font-bold">Account Settings</h1>
                                    <p className="text-blue-100">Manage your account and preferences</p>
                                </div>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="border-b border-light-border dark:border-dark-border">
                            <div className="flex overflow-x-auto">
                                {tabs.map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                                            activeTab === tab.id
                                                ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                        }`}
                                    >
                                        <tab.icon className="h-5 w-5" />
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            {/* Şifre Değiştir */}
                            {activeTab === 'password' && user?.authProvider !== 'google' && (
                                <motion.form
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    onSubmit={handlePasswordChange}
                                    className="space-y-4 max-w-md"
                                >
                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-light-text dark:text-dark-text">
                                            Current Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                value={currentPassword}
                                                onChange={(e) => setCurrentPassword(e.target.value)}
                                                className="w-full px-4 py-2 pr-10 border border-light-border dark:border-dark-border rounded-lg bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                            >
                                                {showCurrentPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-light-text dark:text-dark-text">
                                            New Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showNewPassword ? 'text' : 'password'}
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="w-full px-4 py-2 pr-10 border border-light-border dark:border-dark-border rounded-lg bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text"
                                                required
                                                minLength={6}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                            >
                                                {showNewPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-light-text dark:text-dark-text">
                                            New Password (Tekrar)
                                        </label>
                                        <input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="w-full px-4 py-2 border border-light-border dark:border-dark-border rounded-lg bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text"
                                            required
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                                    >
                                        {loading ? 'Updating...' : 'Change Password'}
                                    </button>
                                </motion.form>
                            )}

                            {/* Email Değiştir - Sadece local kullanıcılar için */}
                            {activeTab === 'email' && user?.authProvider !== 'google' && (
                                <motion.form
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    onSubmit={handleEmailChange}
                                    className="space-y-4 max-w-md"
                                >
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                        <p className="text-sm text-blue-800 dark:text-blue-200">
                                            <ShieldCheckIcon className="h-5 w-5 inline mr-2" />
                                            Current Email: <strong>{user?.email}</strong>
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-light-text dark:text-dark-text">
                                            New Email
                                        </label>
                                        <input
                                            type="email"
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            className="w-full px-4 py-2 border border-light-border dark:border-dark-border rounded-lg bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-light-text dark:text-dark-text">
                                            Confirm Your Password
                                        </label>
                                        <input
                                            type="password"
                                            value={emailPassword}
                                            onChange={(e) => setEmailPassword(e.target.value)}
                                            className="w-full px-4 py-2 border border-light-border dark:border-dark-border rounded-lg bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text"
                                            required
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                                    >
                                        {loading ? 'Updating...' : 'Change Email'}
                                    </button>
                                </motion.form>
                            )}

                            {/* Bildirim Tercihleri */}
                            {activeTab === 'notifications' && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="space-y-4"
                                >
                                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                                        Choose which content you want to receive notifications from
                                    </p>

                                    {[
                                        { key: 'news', label: 'News', desc: 'Notify when new news content is published' },
                                        { key: 'research', label: 'Research', desc: 'Notify when new research content is published' },
                                        { key: 'info', label: 'Aero Facts', desc: 'Notify when new info content is published' },
                                        { key: 'courses', label: 'Courses', desc: 'Yeni Courses eklendiğinde bildir' },
                                        { key: 'email', label: 'Email Notifications', desc: 'Also receive notifications via email' }
                                    ].map(item => (
                                        <div key={item.key} className="flex items-center justify-between p-4 bg-light-bg dark:bg-dark-bg rounded-lg">
                                            <div>
                                                <h3 className="font-medium text-light-text dark:text-dark-text">{item.label}</h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={notificationPrefs[item.key]}
                                                    onChange={(e) => handleNotificationUpdate(item.key, e.target.checked)}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                                            </label>
                                        </div>
                                    ))}
                                </motion.div>
                            )}

                            {/* Delete Account */}
                            {activeTab === 'delete' && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="space-y-4 max-w-md"
                                >
                                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                                        <h3 className="font-semibold text-red-800 dark:text-red-200 mb-2">
                                            ⚠️ Warning!
                                        </h3>
                                        <p className="text-sm text-red-700 dark:text-red-300">
                                            This action cannot be undone. Your account, all course progress and certificates will be permanently deleted.
                                        </p>
                                    </div>

                                    {!showDeleteConfirm ? (
                                        <button
                                            onClick={() => setShowDeleteConfirm(true)}
                                            className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium transition-colors"
                                        >
                                            I Want to Delete My Account
                                        </button>
                                    ) : (
                                        <div className="space-y-4">
                                            {/* Şifre sadece local kullanıcılar için */}
                                            {user?.authProvider !== 'google' && (
                                                <div>
                                                    <label className="block text-sm font-medium mb-2 text-light-text dark:text-dark-text">
                                                        Confirm Your Password
                                                    </label>
                                                    <input
                                                        type="password"
                                                        value={deletePassword}
                                                        onChange={(e) => setDeletePassword(e.target.value)}
                                                        className="w-full px-4 py-2 border border-light-border dark:border-dark-border rounded-lg bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text"
                                                        placeholder="Enter your password to confirm"
                                                    />
                                                </div>
                                            )}

                                            {user?.authProvider === 'google' && (
                                                <p className="text-sm text-yellow-600 dark:text-yellow-400">
                                                    You're about to delete your Google-linked account.
                                                </p>
                                            )}

                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => {
                                                        setShowDeleteConfirm(false);
                                                        setDeletePassword('');
                                                    }}
                                                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg font-medium transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={handleDeleteAccount}
                                                    disabled={loading || !deletePassword}
                                                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                                                >
                                                    {loading ? 'Deleting...' : 'Delete Account'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>
        </>
    );
};

export default SettingsPage;
